import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, ArrowLeft, MapPin, Calendar } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import p1 from '../../assets/p1.webp';
import p2 from '../../assets/p2.webp';
import p3 from '../../assets/p3.webp';
import { projectsApi } from '../../api/projects';
import './Portfolio.css';

const FALLBACK_IMAGES = [p1, p2, p3];

export function Portfolio() {
    const { t, language } = useLanguage();
    const isRtl = language === 'ar';
    const sliderRef = useRef(null);
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const data = await projectsApi.getAll();
                const featuredProjects = Array.isArray(data) 
                    ? data.filter(project => project.isFeatured === true) 
                    : [];
                setProjects(featuredProjects);
            } catch (err) {
                console.error('Failed to fetch projects:', err);
            }
        };
        fetchProjects();
    }, []);

    const scroll = (direction) => {
        if (sliderRef.current) {
            const { scrollLeft, clientWidth } = sliderRef.current;
            sliderRef.current.scrollTo({
                left: direction === 'left'
                    ? scrollLeft - clientWidth * 0.8
                    : scrollLeft + clientWidth * 0.8,
                behavior: 'smooth'
            });
        }
    };

    const IMG_BASE = 'https://ayonkdesigns.com';

    const getTranslatedCategory = (cat) => {
        if (!cat) return '—';
        if (!isRtl) return cat;
        
        const map = {
            'interior': 'تصميم داخلي',
            'construction': 'إنشاءات',
            'facades': 'واجهات',
            'finishing': 'تشطيب'
        };
        return map[cat.toLowerCase()] || cat;
    };

    return (
        <section className="portfolio-section" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="container">

                <div className="portfolio-header-new">
                    <div className="header-left">
                        <div className="label-with-bar gold">
                            <span className="bar"></span>
                            <span className="label-text">
                                {isRtl ? 'معرض الأعمال' : 'PORTFOLIO'}
                            </span>
                        </div>
                        <h2>{isRtl ? 'استكشف إبداعاتنا' : t('portfolio.title')}</h2>
                        <p>{t('portfolio.description')}</p>
                    </div>

                    <div className="slider-controls">
                        <button className="control-btn" onClick={() => scroll('left')}>
                            {isRtl ? <ChevronRight size={22} /> : <ChevronLeft size={22} />}
                        </button>
                        <button className="control-btn" onClick={() => scroll('right')}>
                            {isRtl ? <ChevronLeft size={22} /> : <ChevronRight size={22} />}
                        </button>
                    </div>
                </div>

                <div className="portfolio-slider-wrapper">
                    <div className="portfolio-slider" ref={sliderRef}>
                        {projects.map((project, index) => {
                            const primaryImg = project.images?.find(i => i.isPrimary);
                            const imgUrl = primaryImg
                                ? IMG_BASE + primaryImg.imageUrl
                                : (project.images?.[0]?.imageUrl
                                    ? IMG_BASE + project.images[0].imageUrl
                                    : FALLBACK_IMAGES[index % 3]);

                            const title = (!isRtl && project.titleEn) ? project.titleEn : project.title;
                            const location = (!isRtl && project.locationEn) ? project.locationEn : (project.location || '—');
                            const year = project.completedAt
                                ? new Date(project.completedAt).getFullYear()
                                : '—';

                            return (
                                <div key={project.id} className="project-slide">
                                    <div className="project-card-new">
                                        <div className="pc-image">
                                            <img src={imgUrl} alt={title} />
                                            <span className="pc-tag">
                                                {getTranslatedCategory(project.category || project.finishingType)}
                                            </span>
                                        </div>
                                        <div className="pc-content">
                                            <h3>{title}</h3>
                                            <div className="pc-meta">
                                                <div className="meta-item">
                                                    <MapPin size={14} />
                                                    <span>{location}</span>
                                                </div>
                                                <div className="meta-item">
                                                    <Calendar size={14} />
                                                    <span>{year}</span>
                                                </div>
                                            </div>
                                            <Link to={`/portfolio/${project.id}`} className="pc-link">
                                                <span>{t('portfolio.viewDetails')}</span>
                                                {isRtl ? <ArrowLeft size={15} /> : <ArrowRight size={15} />}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="view-all-center">
                    <Link to="/portfolio" className="btn-view-all-lux">
                        {isRtl ? 'مشاهدة معرض الأعمال كاملاً' : 'View Full Portfolio'}
                    </Link>
                </div>

            </div>
        </section>
    );
}
